import { useId } from 'react';
import { Plus, Trash2, GripVertical } from 'lucide-react';

interface DynamicListProps<T> {
  label: string;
  items: T[];
  renderItem: (item: T, index: number, onChange: (item: T) => void, onRemove: () => void) => React.ReactNode;
  onAdd: () => void;
  onChange: (items: T[]) => void;
  emptyText?: string;
}

export function DynamicList<T>({ label, items, renderItem, onAdd, onChange, emptyText = '暫無項目' }: DynamicListProps<T>) {
  const id = useId();

  const handleMove = (from: number, to: number) => {
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  return (
    <div className="mb-4" id={id}>
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{label}</span>
        <button
          type="button"
          onClick={onAdd}
          className="flex items-center gap-1 px-3 py-1.5 text-sm rounded text-white"
          style={{ background: 'var(--pf-primary, #3b82f6)' }}
        >
          <Plus size={14} /> 新增
        </button>
      </div>
      {items.length === 0 ? (
        <div className="p-4 text-center text-sm text-slate-500 dark:text-slate-400 border border-dashed border-slate-300 dark:border-slate-600 rounded-lg">
          {emptyText}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item, index) => (
            <div key={index} className="flex gap-2 items-start p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
              <div className="flex flex-col gap-1 pt-1">
                <button
                  type="button"
                  onClick={() => handleMove(index, index - 1)}
                  disabled={index === 0}
                  className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
                >
                  <GripVertical size={16} />
                </button>
              </div>
              <div className="flex-1">
                {renderItem(
                  item,
                  index,
                  (updated) => {
                    const next = [...items];
                    next[index] = updated;
                    onChange(next);
                  },
                  () => {
                    const next = [...items];
                    next.splice(index, 1);
                    onChange(next);
                  }
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  const next = [...items];
                  next.splice(index, 1);
                  onChange(next);
                }}
                className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
